import type { CreatePollData, Poll, PollOption } from '@features/polls/types/poll.types';
import { UserProfile } from '@shared/types/user';
import { Timestamp } from 'firebase/firestore';

/**
 * Polls of spaces are ordinary polls; the names are kept so the rest of this
 * file and its callers did not have to change.
 */
export type SpacePollOption = PollOption;
export type SpacePoll = Poll;
export type CreateSpacePollData = CreatePollData;

/**
 * Space visibility options
 */
export enum SpaceVisibility {
	PUBLIC = 'public',
	PRIVATE = 'private',
	CONNECTIONS = 'connections',
}

/**
 * Sort types for spaces list
 */
export enum SortType {
	POPULAR = 'popular',
	NEWEST = 'newest',
	FOLLOWING = 'following',
}

/**
 * Media variant for different resolutions and formats
 */
export interface MediaVariant {
	url: string;
	width?: number;
	height?: number;
	quality?: string;
	format: string;
	type: 'original' | 'thumbnail' | 'compressed' | 'web';
}

/**
 * Media item for images and videos
 */
export interface MediaItem {
	id?: string;
	type: 'image' | 'video' | 'document';
	variants: MediaVariant[];
}

/**
 * Space data structure
 */
export interface Space {
	id?: string;
	author: UserProfile;
	title: string;
	content: string;
	media?: MediaItem[];
	poll?: SpacePoll;
	upvotes: number;
	comments: number;
	commentCount: number;
	isUpvoted?: boolean;
	isFollowing?: boolean;
	isPinned?: boolean;
	pinnedAt?: Timestamp | null;
	commentList?: SpaceComment[];
	createdAt: Timestamp;
	updatedAt: Timestamp;
	isPrivate?: boolean;
	allowedUsers?: string[];
	allowedUsersProfiles?: Partial<UserProfile>[];
}

/**
 * Space comment data structure
 */
export interface SpaceComment {
	id?: string;
	spaceId: string;
	parentId?: string;
	author: Partial<UserProfile>;
	content: string;
	upvotes: number;
	isUpvoted?: boolean;
	replies?: SpaceComment[];
	createdAt?: Timestamp;
	updatedAt?: Timestamp;
}

/**
 * Data for creating a new space
 */
export interface CreateSpaceData {
	title: string;
	content: string;
	visibility: SpaceVisibility;
	media?: MediaItem[];
	/** `null` removes the poll the space has. */
	poll?: CreateSpacePollData | null;
	isPrivate?: boolean;
	allowedUsers?: string[];
}

/**
 * Data for creating a new comment
 */
export interface SpaceCommentData {
	content: string;
	parentId?: string;
}

/**
 * Response structure for spaces list
 */
export interface SpacesResponse {
	spaces: Space[];
	total: number;
	currentPage: number;
	hasNextPage: boolean;
}

/**
 * Response structure for comments list
 */
export interface CommentsResponse {
	comments: SpaceComment[];
	total: number;
	currentPage: number;
	hasNextPage: boolean;
}

/**
 * Response for upvote toggle
 */
export interface UpvoteResponse {
	isUpvoted: boolean;
}

/**
 * Response for follow toggle
 */
export interface FollowResponse {
	isFollowing: boolean;
}
